import { isEmpty } from "lodash";
import { Fragment, useEffect, useRef } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { CircularProgress } from "@/Components/v2/atoms/Progress/CircularProgress";
import { Stack } from "@/Components/v2/atoms/Stack";
import NoData from "@/Components/v2/molecule/NoData";
import Header from "@/Components/v2/organism/Header/Header";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useConfig } from "@/hooks/useConfig";
import { Toggle, ToolBarShared } from "@/shared/components/DataGrid/ToolBarShared";
import DataGridContainer from "@/shared/components/DataGridContainer";
import { useDateRangeStore } from "@/shared/components/date-range/store/useDateRangeStore";
import Summary from "@/shared/components/Sections/Summary";
import Title from "@/shared/components/Sections/Title";
import { useMetricOptions } from "@/shared/hooks/useMetricOptions";
import { BusinessUnitToggle, MetricType, ViewToggle } from "@/shared/types";
import { Layer } from "@/Utils/hierarchy";

import { useGeographiesROIs } from "./api/getGeographiesROIs";
import BreakdownV2 from "./components/Sections/BreakdownV2";
import { content, noDataContent } from "./constants/content";
import { DEFAULT_COLUMNS } from "./constants/defaults";
import { getColumnDefinitions } from "./getColumnDefinitions";
import { useGeographiesStore } from "./store/useGeographiesStore";
import { useAvailableFilters } from "./useAvailableFilters";

interface GeographiesProps {}

const Geographies: React.FC<GeographiesProps> = () => {
  const { config } = useConfig();
  const { nodes, loading } = useInfo();
  const { startDate, endDate } = useDateRangeStore();
  const { geographies, channels, targetNode } = useGeographiesStore((state) => state.filters);
  const { setTargetNode, setActiveColumns, setGridView, setBusinessUnitToggle, setMetricType } =
    useGeographiesStore();
  const { gridView, activeColumns, businessUnitToggle, metricType } = useGeographiesStore(
    (state) => state.grid
  );
  const { chartView } = useGeographiesStore((state) => state.chart);
  const apiRef = useRef(null);

  const currencySymbol = config.currency.getSymbol();
  const formatCurrency = config.currency.formatCurrency;
  const metricOptions = useMetricOptions();

  useEffect(() => {
    if (nodes && !loading && targetNode === null) {
      const defaultNodeUUID = nodes?.getDefaultNode()?.node.uuid;
      if (defaultNodeUUID) {
        setTargetNode(defaultNodeUUID);
      }
    }
  }, [loading, targetNode, nodes, setTargetNode]);

  const selectedNode = nodes?.getNode(targetNode || "");

  const { data: geographiesROIsV3, isLoading: isGeographiesLoading } = useGeographiesROIs({
    brand: selectedNode?.node.brand ?? "default",
    category: selectedNode?.node.category ?? "default",
    product: selectedNode?.node.product ?? "default",
    channels: channels,
    startDate: startDate ?? "",
    endDate: endDate ?? "",
    geographies: geographies,
    graphView: chartView,
    tableView: gridView,
    businessUnitToggle: businessUnitToggle,
    metricType: metricType,
    config: {
      enabled:
        !!selectedNode &&
        !!startDate &&
        !!endDate &&
        !!chartView &&
        !!gridView &&
        !!businessUnitToggle &&
        !!metricType,
    },
  });

  const columns = getColumnDefinitions(activeColumns, {
    currencySymbol,
    formatCurrency,
    gridView,
    businessUnitToggle,
    metricType,
    config,
  });

  const availableFilters = useAvailableFilters({
    selectedNode,
    startDate: startDate ?? "",
    endDate: endDate ?? "",
  });

  const { moduleOpened } = useAnalytics();
  useEffect(() => {
    moduleOpened("geography", Layer.Portfolio);
  }, []);

  const showWelcomeScreen = !geographies || geographies.length === 0 || channels.length === 0;

  const isLoading = !nodes || loading || isGeographiesLoading;

  const toggles: Toggle[] = [
    {
      id: "viewToggle",
      selectedValue: gridView,
      options: [
        { label: "Geographies", value: ViewToggle.INSIGHT },
        { label: "Channels", value: ViewToggle.CHANNEL },
      ],
    },
    {
      id: "businessUnitToggle",
      selectedValue: businessUnitToggle,
      options: [
        { label: "Aggregate Business Units", value: "aggregate" },
        { label: "Split by Business Unit", value: "split" },
      ],
    },
  ];

  const metricDropdownConfig = {
    label: "Show",
    value: metricType,
    options: metricOptions,
    onChange: (value: string | null) => {
      if (value) {
        setMetricType(value as MetricType);
      }
    },
  };

  return (
    <Box>
      <Title title={content.title} subtitle={content.subtitle} />
      <Header availableFilters={availableFilters} />
      {isLoading ? (
        <Stack justifyContent="center" alignItems="center" sx={{ minHeight: "50rem" }}>
          <CircularProgress size={150} />
        </Stack>
      ) : isEmpty(geographiesROIsV3?.chartView) || isEmpty(geographiesROIsV3?.totals) ? (
        <NoData title={noDataContent.title} description={noDataContent.description} />
      ) : (
        <Fragment>
          <Box sx={{ marginX: 4 }}>
            <Summary
              spend={geographiesROIsV3?.totals.totalSpend ?? 0}
              revenue={geographiesROIsV3?.totals.totalRevenue ?? 0}
              roi={geographiesROIsV3?.totals.totalRoi ?? 0}
              formatCurrency={formatCurrency}
            />
            <BreakdownV2
              title={content.filterLabel}
              showWelcomeScreen={showWelcomeScreen}
              totalSpend={geographiesROIsV3?.totals.totalSpend ?? 0}
              totalRevenue={geographiesROIsV3?.totals.totalRevenue ?? 0}
              currencySymbol={currencySymbol}
              data={geographiesROIsV3?.chartView ?? []}
            />
            <DataGridContainer
              apiRef={apiRef}
              rows={geographiesROIsV3?.tableView ?? []}
              columns={columns}
              metricDropdown={metricDropdownConfig}
              toolbarComponent={
                <ToolBarShared
                  apiRef={apiRef}
                  columns={columns}
                  activeColumns={activeColumns}
                  toggles={toggles}
                  handleApplyFilters={({ activeColumns, toggles }) => {
                    const gridViewToggle = toggles["viewToggle"];
                    const businessUnitToggle = toggles["businessUnitToggle"];
                    setActiveColumns(activeColumns);
                    setGridView(gridViewToggle as ViewToggle);

                    if (businessUnitToggle) {
                      setBusinessUnitToggle(businessUnitToggle as BusinessUnitToggle);
                    }
                  }}
                  handleResetFilters={() => {
                    setActiveColumns(DEFAULT_COLUMNS);
                    setGridView(ViewToggle.INSIGHT);
                    setMetricType(MetricType.MARKETING_ROI);
                    setBusinessUnitToggle(BusinessUnitToggle.AGGREGATE);
                  }}
                />
              }
              options={{
                rowHeight: 52,
                sortModel: [{ field: "total_spend", sort: "desc" }],
              }}
            />
          </Box>
        </Fragment>
      )}
    </Box>
  );
};

export default Geographies;
